/** The catalogue-sync part of Product Sync (backend plan 2026-10-08 §3.9), shown for an
 *  integration whose restech has a catalogue settings registry (Ventrata first).
 *
 *  Rows come from `Restech_Catalogue_Product`, refreshed by the `list_products` task (the
 *  page's "Refresh listing" = a forced Fetch), never by a live vendor fetch per view. Per
 *  product, ops decide what a sync builds — selection, kind, tours per product or per option,
 *  package import, naming, itinerary and content — and every value shows where it comes from.
 *
 *  A tenant whose gated restech is not opted in (`catalogue_sync_disabled`) sees only the
 *  gate: live tenants keep mapping their products by hand in the listing below. */
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import type { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import * as catalogueApi from '@/api/catalogue';
import type {
  CatalogueOutput,
  CatalogueRegistry,
  CatalogueRow,
  CatalogueRowFilters,
  CatalogueRowPage,
  CatalogueScope,
} from '@/api/catalogue';
import type { BatchActionContext, BatchActionDef } from '@/components/DataGrid/batchTypes';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { BULK_MAX, BulkCouplingDialog, BulkSettingDialog, type BulkTarget } from './BulkDialogs';
import { CatalogueFilters } from './CatalogueFilters';
import { CatalogueGateAlert, CoverageAlert, PackageDriftAlert, TenantGateNotes } from './CatalogueBanners';
import { CATALOGUE_PAGE_SIZES, CatalogueGrid, orderingOf } from './CatalogueGrid';
import type { CatalogueColumnDeps } from './catalogueColumns';
import { explainTargetInUse, specsByKey } from './catalogueModel';
import { CouplingDialog } from './CouplingDialog';
import { CATALOGUE_STORY } from './help';
import { ProductSettingsDrawer } from './ProductSettingsDrawer';
import { ReviewQueue } from './ReviewQueue';
import { RunDetailDialog } from './RunDetailDialog';
import { RunsTable } from './RunsTable';
import { TenantSettingsForm } from './TenantSettingsForm';
import { useCatalogueRows, usePatchProductSettings } from './queries';

const PERM = 'console_bg_sync_products';

type SectionTab = 'products' | 'review' | 'runs' | 'settings';

/** What the launch panel imports: the ticked rows, every matching row, or every included one.
 *  Ids are vendor product ids (what `/ops/sync_products/start/` takes, at most 500). */
export interface LaunchTarget {
  count: number;
  noun: string;
  resolveIds: () => Promise<string[]>;
}

export interface CatalogueSectionProps {
  scope: CatalogueScope;
  registry: CatalogueRegistry;
  /** The integration's config dialog (where the gate's config key is edited). */
  configUrl: string | null;
  /** A listing refresh (forced `list_products`) is running. */
  refreshing: boolean;
  onRefresh: () => void;
  /** The host's launch panel (import options + button) for the current target. */
  renderLaunch?: (target: LaunchTarget) => ReactNode;
}

async function resolveRows(
  scope: CatalogueScope,
  filters: CatalogueRowFilters,
  what: string,
): Promise<CatalogueRow[]> {
  const page = await catalogueApi.listRows(scope, { ...filters, page: 1, page_size: BULK_MAX });
  if ((page.count ?? 0) > BULK_MAX) {
    throw new Error(`${page.count} ${what} — at most ${BULK_MAX} at a time; narrow the filters.`);
  }
  return page.results;
}

export function CatalogueSection({
  scope,
  registry,
  configUrl,
  refreshing,
  onRefresh,
  renderLaunch,
}: CatalogueSectionProps) {
  const { show } = useSnackbar();
  const specs = useMemo(() => specsByKey(registry), [registry]);

  const [filters, setFilters] = useState<CatalogueRowFilters>({});
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: CATALOGUE_PAGE_SIZES[1]!,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);
  // The rows behind the ticked ids — kept across pages (`keepNonExistentRowsSelected`).
  const [selectedRows, setSelectedRows] = useState<Map<number, CatalogueRow>>(new Map());
  const [drawerFor, setDrawerFor] = useState<number | null>(null);
  const [coupling, setCoupling] = useState<{ row: CatalogueRow; output: CatalogueOutput } | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<number>>(new Set());
  const [tab, setTab] = useState<SectionTab>('products');
  const [runOpen, setRunOpen] = useState<number | null>(null);

  const queryFilters = useMemo(
    () => ({ ...filters, ...(search.trim() ? { search: search.trim() } : {}) }),
    [filters, search],
  );
  const params = useMemo(
    () => ({
      ...queryFilters,
      page: pagination.page + 1,
      page_size: pagination.pageSize,
      ...(orderingOf(sortModel) ? { ordering: orderingOf(sortModel) } : {}),
    }),
    [queryFilters, pagination, sortModel],
  );
  const query = useCatalogueRows(scope, params);
  const rows = useMemo(() => query.data?.results ?? [], [query.data]);
  const scopeKey = `${scope.tenantId}|${scope.integration}|${scope.account ?? ''}`;
  // A refused filter (400 `invalid_filter` / `filter_too_broad`) leaves the query without data —
  // placeholder data only covers the loading — and the page would lose its filter bar with it.
  // It stands on the last page this scope loaded instead (`meta` ignores the filters), so the
  // filters stay on screen to change; the grid shows no rows.
  const lastLoaded = useRef<{ scopeKey: string; page: CatalogueRowPage } | null>(null);
  if (query.data) lastLoaded.current = { scopeKey, page: query.data };
  const filterRefused = query.isError && catalogueApi.isFilterRefusal(query.error);
  const meta =
    query.data?.meta ??
    (filterRefused && lastLoaded.current?.scopeKey === scopeKey ? lastLoaded.current.page.meta : undefined);

  // A new scope or a new query starts from a clean slate: page 1, nothing selected.
  useEffect(() => {
    setFilters({});
    setSearch('');
  }, [scopeKey]);
  const identity = JSON.stringify([scopeKey, queryFilters]);
  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
    setSelection([]);
    setSelectedRows(new Map());
    setAllMatching(false);
  }, [identity]);

  const onSelectionChange = (model: GridRowSelectionModel) => {
    setAllMatching(false);
    setSelection(model);
    setSelectedRows((prev) => {
      const next = new Map<number, CatalogueRow>();
      for (const id of model) {
        const n = Number(id);
        const row = rows.find((r) => r.id === n) ?? prev.get(n);
        if (row) next.set(n, row);
      }
      return next;
    });
  };
  const clearSelection = () => {
    setSelection([]);
    setSelectedRows(new Map());
    setAllMatching(false);
  };

  const rowsByVendor = useMemo(() => new Map(rows.map((r) => [r.vendor_id, r])), [rows]);
  const patch = usePatchProductSettings(scope.tenantId);
  const onSet = (row: CatalogueRow, settings: Record<string, unknown>) => {
    setBusy((b) => new Set(b).add(row.id));
    patch.mutate(
      { productId: row.id, body: { settings } },
      {
        onSuccess: (r) => {
          for (const w of r.warnings) show(w.detail, 'warning', { autoHideDuration: 15000 });
        },
        onError: (e) => {
          // Products join this one's tour (join_target_in_use): named from its row, with what to do.
          const refusals = catalogueApi.settingErrorsOf(e);
          const explained = refusals?.length
            ? explainTargetInUse(
                refusals,
                (id) => (id === row.id ? row.joined_by : undefined),
                (v) => rowsByVendor.get(v),
              )
            : null;
          show(
            explained
              ? `${row.title || row.name}: ${explained.map((x) => x.detail).join(' ')}`
              : (e as Error).message,
            'error',
            { autoHideDuration: 12000 },
          );
        },
        onSettled: () =>
          setBusy((b) => {
            const next = new Set(b);
            next.delete(row.id);
            return next;
          }),
      },
    );
  };

  // The columns are memoised; they call the latest writer through a ref.
  const onSetRef = useRef(onSet);
  onSetRef.current = onSet;

  const coverageById = useMemo(
    () => new Map((meta?.coverage_warnings ?? []).map((w) => [w.id, w])),
    [meta],
  );
  const deps: CatalogueColumnDeps = useMemo(
    () => ({
      specs,
      onSet: (row, settings) => onSetRef.current(row, settings),
      onOpen: (row) => setDrawerFor(row.id),
      onCoupling: (row, output) => setCoupling({ row, output }),
      busy,
      coverageById,
      scope,
      rowsByVendor,
    }),
    // `scope` by its identity: the host builds a new object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [specs, busy, coverageById, scopeKey, rowsByVendor],
  );

  const resolveSelection = (): Promise<CatalogueRow[]> =>
    allMatching
      ? resolveRows(scope, queryFilters, 'products match')
      : Promise.resolve(
          selection.map((id) => selectedRows.get(Number(id))).filter((r): r is CatalogueRow => !!r),
        );
  const bulkTarget = (ctx: BatchActionContext): BulkTarget => ({
    count: ctx.count,
    phrase: allMatching
      ? `${ctx.matchedPhrase} products`
      : `${ctx.count} selected product${ctx.count === 1 ? '' : 's'}`,
    resolve: resolveSelection,
  });
  const done = (ctx: BatchActionContext) => () => {
    ctx.clearSelection();
    ctx.close();
  };
  const batchActions: BatchActionDef[] = [
    {
      key: 'include',
      label: 'Include in sync',
      permission: PERM,
      dialog: (ctx) => (
        <BulkSettingDialog tenantId={scope.tenantId} scope={scope} registry={registry} mode="include" target={bulkTarget(ctx)} rowsByVendor={rowsByVendor} onClose={ctx.close} onDone={done(ctx)} />
      ),
    },
    {
      key: 'exclude',
      label: 'Exclude from sync',
      permission: PERM,
      dialog: (ctx) => (
        <BulkSettingDialog tenantId={scope.tenantId} scope={scope} registry={registry} mode="exclude" target={bulkTarget(ctx)} rowsByVendor={rowsByVendor} onClose={ctx.close} onDone={done(ctx)} />
      ),
    },
    {
      key: 'setting',
      label: 'Set a setting…',
      permission: PERM,
      dialog: (ctx) => (
        <BulkSettingDialog tenantId={scope.tenantId} scope={scope} registry={registry} mode="setting" target={bulkTarget(ctx)} rowsByVendor={rowsByVendor} onClose={ctx.close} onDone={done(ctx)} />
      ),
    },
    {
      key: 'decouple',
      label: 'Decouple outputs…',
      permission: PERM,
      dialog: (ctx) => (
        <BulkCouplingDialog tenantId={scope.tenantId} coupled={false} target={bulkTarget(ctx)} onClose={ctx.close} onDone={done(ctx)} />
      ),
    },
    {
      key: 'recouple',
      label: 'Re-couple outputs…',
      permission: PERM,
      dialog: (ctx) => (
        <BulkCouplingDialog tenantId={scope.tenantId} coupled target={bulkTarget(ctx)} onClose={ctx.close} onDone={done(ctx)} />
      ),
    },
  ];

  const categories = useMemo(
    () =>
      Array.from(
        new Set(rows.flatMap((r) => r.categories.map((c) => c.title).filter((t): t is string => !!t))),
      ).sort(),
    [rows],
  );

  if (query.isLoading) return <LinearProgress sx={{ mb: 2 }} aria-label="Loading the catalogue" />;
  if (query.isError && !meta) {
    return (
      <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => void query.refetch()}>Retry</Button>}>
        {(query.error as Error).message}
      </Alert>
    );
  }
  if (!meta) return null;

  // Not opted in: the gate is all there is to say (and the hand-mapping listing still works).
  if (!meta.gate.can_import && meta.gate.import_blocker?.code === 'catalogue_sync_disabled') {
    return <CatalogueGateAlert meta={meta} integration={scope.integration} configUrl={configUrl} />;
  }

  const counts = meta.counts;
  const pendingReview = meta.pending_review;
  const previewSupported = meta.gate.preview_supported;
  const total = counts.total;
  const launchTarget: LaunchTarget =
    allMatching || selection.length > 0
      ? {
          count: allMatching ? (query.data?.count ?? 0) : selection.length,
          noun: allMatching ? 'matching products' : selection.length === 1 ? 'selected product' : 'selected products',
          resolveIds: async () => (await resolveSelection()).map((r) => r.vendor_id),
        }
      : {
          count: counts.included,
          noun: 'included products',
          resolveIds: async () =>
            (await resolveRows(scope, { include: true, removed: false }, 'included products')).map((r) => r.vendor_id),
        };

  return (
    <Box sx={{ mb: 2 }}>
      <CatalogueGateAlert meta={meta} integration={scope.integration} configUrl={configUrl} />

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {total.toLocaleString()} products · {counts.included.toLocaleString()} included ·{' '}
          {counts.packages.toLocaleString()} packages
          {counts.removed ? ` · ${counts.removed.toLocaleString()} removed upstream` : ''} ·{' '}
          <span title={formatDate(meta.last_listing_ts)}>
            {meta.last_listing_ts ? `listed ${formatRelativeTime(meta.last_listing_ts)}` : 'never listed'}
          </span>
        </Typography>
        <Button
          size="small"
          startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
          disabled={refreshing}
          onClick={onRefresh}
        >
          {refreshing ? 'Refreshing listing…' : 'Refresh listing'}
        </Button>
        <HelpLink story={CATALOGUE_STORY} />
      </Stack>

      <TenantGateNotes meta={meta} />

      <Tabs value={tab} onChange={(_, v: SectionTab) => setTab(v)} sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}>
        <Tab value="products" label="Products" sx={{ minHeight: 40 }} />
        <Tab
          value="review"
          sx={{ minHeight: 40 }}
          aria-label={`Review queue, ${pendingReview} waiting`}
          label={
            <Badge color="warning" badgeContent={pendingReview} max={999} sx={{ '& .MuiBadge-badge': { right: -12 } }}>
              Review queue
            </Badge>
          }
        />
        <Tab value="runs" label="AI runs" sx={{ minHeight: 40 }} />
        <Tab value="settings" label="Tenant settings" sx={{ minHeight: 40 }} />
      </Tabs>

      {tab === 'settings' && (
        <TenantSettingsForm tenantId={scope.tenantId} restech={scope.integration} registry={registry} />
      )}

      {tab === 'review' && (
        <ReviewQueue scope={scope} previewSupported={previewSupported} onOpenProduct={setDrawerFor} />
      )}

      {tab === 'runs' && (
        <RunsTable tenantId={scope.tenantId} scope={scope} onOpen={(r) => setRunOpen(r.id)} />
      )}

      {tab === 'products' && <PackageDriftAlert meta={meta} />}
      {tab === 'products' && <CoverageAlert tenantId={scope.tenantId} warnings={meta.coverage_warnings} />}

      {tab !== 'products' ? null : total === 0 ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" disabled={refreshing} onClick={onRefresh}>
              Refresh listing
            </Button>
          }
        >
          No products in this catalogue yet. Refresh the listing to load them from {scope.integration}; it runs
          in the background and the rows appear when it completes.
        </Alert>
      ) : (
        <>
          <CatalogueFilters registry={registry} value={filters} onChange={setFilters} categories={categories} />
          {query.isError &&
            (catalogueApi.isFilterTooBroad(query.error) ? (
              <Alert
                severity="warning"
                sx={{ mb: 1.5 }}
                action={
                  filters.setting ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setFilters((f) => ({ ...f, setting: undefined, setting_source: undefined }))}
                    >
                      Clear setting filter
                    </Button>
                  ) : undefined
                }
              >
                <AlertTitle>Narrow the filter</AlertTitle>
                Too many products match to filter them by a setting, which is worked out product by product.
                Narrow the other filters first — kind, category, selection, packages, upstream, listing or
                the search — or clear the setting filter.
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {(query.error as Error).message}
              </Alert>
            ))}
          <CatalogueGrid
            query={query}
            deps={deps}
            pagination={pagination}
            onPaginationChange={setPagination}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            search={search}
            onSearchChange={setSearch}
            selection={selection}
            onSelectionChange={onSelectionChange}
            allMatching={allMatching}
            onSelectAllMatching={() => setAllMatching(true)}
            onClearSelection={clearSelection}
            batchActions={batchActions}
            emptyMessage={filterRefused ? 'Change the filters to list products.' : 'No product matches these filters.'}
          />
          {meta.gate.can_import && renderLaunch && <Box sx={{ mt: 2 }}>{renderLaunch(launchTarget)}</Box>}
        </>
      )}

      {runOpen !== null && (
        <RunDetailDialog tenantId={scope.tenantId} runId={runOpen} registry={registry} onClose={() => setRunOpen(null)} />
      )}

      {drawerFor !== null && (
        <ProductSettingsDrawer
          // A new product starts a new draft.
          key={drawerFor}
          tenantId={scope.tenantId}
          productId={drawerFor}
          registry={registry}
          scope={scope}
          previewSupported={previewSupported}
          onClose={() => setDrawerFor(null)}
          onOpenProduct={setDrawerFor}
        />
      )}

      {coupling && (
        <CouplingDialog
          tenantId={scope.tenantId}
          output={coupling.output}
          productName={coupling.row.title || coupling.row.name}
          previewSupported={previewSupported}
          onClose={() => setCoupling(null)}
        />
      )}
    </Box>
  );
}
